import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  ChartBarIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  StarIcon,
  ArrowLeftIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';

const ContractorStatsView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [period, setPeriod] = useState('month');

  // 소장 정보 조회
  const { data: contractor } = useQuery(
    ['contractor', id],
    () => api.get(`/contractors/${id}`).then(res => res.data)
  );

  // 통계 데이터 조회
  const { data: stats, isLoading } = useQuery(
    ['contractor-stats-view', id, period],
    () => api.get(`/contractors/${id}/stats`, { params: { period } }).then(res => res.data),
    {
      enabled: !!id
    }
  );

  // 리뷰 데이터 조회
  const { data: reviews } = useQuery(
    ['contractor-reviews', id],
    () => api.get(`/contractors/${id}/reviews`, { params: { limit: 5 } }).then(res => res.data),
    {
      enabled: !!id
    }
  );

  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-5 w-5 text-gray-300" />;
      }
    });
  };

  if (user?.user_type !== 'admin') {
    navigate('/projects');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">통계를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  const periodLabels = {
    month: '이번 달',
    quarter: '이번 분기',
    year: '올해',
    all: '전체'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-b from-blue-600 to-blue-700 text-white pb-32">
        <div className="px-4 pt-4 pb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <UserCircleIcon className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">{contractor?.name} 소장님 통계</h1>
                <p className="text-blue-100">{contractor?.company_name}</p>
              </div>
            </div>
            <CalendarIcon className="h-6 w-6 text-blue-200" />
          </div>

          {/* 기간 선택 */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {Object.entries(periodLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  period === key
                    ? 'bg-white text-blue-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 주요 통계 */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
              <p className="text-blue-100 text-sm mb-1">총 수익</p>
              <p className="text-2xl font-bold">
                {formatCurrency(stats?.totalAmount || 0)}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
              <p className="text-blue-100 text-sm mb-1">완료율</p>
              <p className="text-2xl font-bold">{stats?.completionRate || 0}%</p>
              <p className="text-xs text-blue-100 mt-1">
                {stats?.completed || 0}/{stats?.total || 0}건
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 통계 */}
      <div className="px-4 -mt-20">
        {/* 계약 성과 지표 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-amber-500" />
            계약 성과 지표
          </h2>

          {/* 계약 성사율 */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">계약 성사율</h3>
              <span className="text-xs text-gray-500">{periodLabels[period]}</span>
            </div>
            
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-bold text-blue-600">
                {stats?.contractRate || 0}%
              </span>
              {stats?.contractRateChange !== undefined && (
                <span className={`text-sm font-medium flex items-center gap-1 ${
                  stats.contractRateChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stats.contractRateChange >= 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4" />
                  )}
                  {Math.abs(stats.contractRateChange)}%
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats?.received || 0}</p>
                <p className="text-xs text-gray-600">전체 접수</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{(stats?.contracted || 0) + (stats?.progress || 0) + (stats?.completed || 0)}</p>
                <p className="text-xs text-gray-600">계약 성사</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{stats?.completed || 0}</p>
                <p className="text-xs text-gray-600">완료</p>
              </div>
            </div>
          </div>

          {/* 세부 현황 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">평균 계약 금액</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency(stats?.averageContractAmount || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">완료율</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {stats?.completionRate || 0}% ({stats?.completed || 0}/{(stats?.contracted || 0) + (stats?.progress || 0) + (stats?.completed || 0)})
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">취소율</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {stats?.cancelRate || 0}% ({stats?.cancelled || 0}건)
              </span>
            </div>
          </div>
        </div>

        {/* 응답 속도 관리 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ClockIcon className="h-5 w-5 text-orange-500" />
            응답 속도 관리
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-orange-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">평균 첫 응답</p>
              <p className="text-2xl font-bold text-orange-600">
                {stats?.avgResponseTime || 0}시간
              </p>
            </div>
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">24시간 내 응답률</p>
              <p className="text-2xl font-bold text-green-600">
                {stats?.responseRate24h || 0}%
              </p>
            </div>
          </div>

          {stats?.pendingCount > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-800">미응답 접수 건</p>
                  <p className="text-xs text-red-600 mt-1">빠른 응답이 필요합니다!</p>
                </div>
                <span className="text-2xl font-bold text-red-600">
                  {stats.pendingCount}건
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 평점 및 리뷰 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-yellow-500" />
            평점 및 리뷰
          </h2>

          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(contractor?.rating || 0)}
                </div>
                <p className="text-3xl font-bold text-gray-900">
                  {contractor?.rating?.toFixed(1) || '0.0'}
                </p>
                <p className="text-sm text-gray-600">평균 평점</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-gray-900">{reviews?.total || 0}</p>
                <p className="text-sm text-gray-600">전체 리뷰</p>
              </div>
            </div>
          </div>

          {/* 최근 리뷰 */}
          {reviews?.reviews && reviews.reviews.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700 mb-2">최근 리뷰</p>
              {reviews.reviews.slice(0, 3).map((review) => (
                <div key={review.id} className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{review.customer_name}</p>
                      <p className="text-xs text-gray-500">{review.project_title}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <StarIconSolid 
                          key={i} 
                          className={`h-4 w-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-200'}`} 
                        />
                      ))}
                    </div>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-gray-700 line-clamp-2">{review.comment}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 월별 수익 추이 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-20">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            수익 현황
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">이번 달 수익</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(stats?.monthlyRevenue || 0)}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">전체 수익</p>
              <p className="text-xl font-bold text-blue-600">
                {formatCurrency(stats?.totalRevenue || 0)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractorStatsView;